import React, { useRef, useState, useEffect } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

export const VoiceTextarea = ({ value, onChange, placeholder, rows = 8, className = '', testid = 'voice-textarea' }) => {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const valueRef = useRef(value);
  const listeningRef = useRef(false);
  valueRef.current = value;

  const supported = typeof window !== 'undefined' && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);

  useEffect(() => () => {
    listeningRef.current = false;
    if (recRef.current) try { recRef.current.stop(); } catch (e) { /* noop */ }
  }, []);

  const stop = () => {
    listeningRef.current = false;
    setListening(false);
    if (recRef.current) try { recRef.current.stop(); } catch (e) { /* noop */ }
  };

  const start = () => {
    if (!supported) {
      toast.error('Voice input is not supported on this device/browser');
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = 'en-CA';
    rec.onresult = (event) => {
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) finalText += event.results[i][0].transcript;
      }
      if (finalText.trim()) {
        const base = valueRef.current ? valueRef.current.replace(/\s+$/, '') + ' ' : '';
        onChange(base + finalText.trim());
      }
    };
    rec.onerror = (event) => {
      if (event.error === 'not-allowed') {
        toast.error('Microphone permission denied. Allow mic access in your settings.');
        stop();
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        toast.error('Voice input error: ' + event.error);
        stop();
      }
    };
    rec.onend = () => {
      if (listeningRef.current) {
        try { rec.start(); } catch (e) { stop(); }
      }
    };
    recRef.current = rec;
    listeningRef.current = true;
    setListening(true);
    try { rec.start(); } catch (e) { stop(); }
  };

  return (
    <div className="relative">
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={listening ? 'Listening… speak now' : placeholder}
        className={`${className} pr-14`}
        rows={rows}
        data-testid={testid}
      />
      <Button
        type="button"
        size="sm"
        onClick={listening ? stop : start}
        className={`absolute bottom-2 right-2 rounded-full w-11 h-11 p-0 shadow-md ${
          listening ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-blue-600 hover:bg-blue-700'
        }`}
        style={{ color: '#ffffff' }}
        title={listening ? 'Stop voice input' : 'Speak your notes'}
        data-testid={`${testid}-mic-btn`}
      >
        {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
      </Button>
      {listening && (
        <p className="text-xs font-semibold mt-1" style={{ color: '#dc2626' }} data-testid={`${testid}-listening`}>
          ● Listening — tap the mic again when you&apos;re done
        </p>
      )}
    </div>
  );
};

export default VoiceTextarea;
